import { StyleSheet } from '@react-pdf/renderer'
import type { Style } from '@react-pdf/stylesheet'
import {
  SHEET_COLORS,
  SHEET_SIZES,
} from 'features/knowledgeDocumentViewer/artifacts/sheet/sheetTheme'

import type { PdfFontSet } from './pdfFonts'

const INCH = 72
export const PAGE_MARGIN_X = 0.6 * INCH
export const PAGE_MARGIN_Y = 0.55 * INCH

const BODY_TEXT = { fontSize: SHEET_SIZES.body, lineHeight: 1.25 } as const
const SMALL_TEXT = { fontSize: SHEET_SIZES.small, lineHeight: 1.25 } as const

/** The sheet typography in react-pdf styles, on the given font set. */
export function createPdfStyles(fonts: PdfFontSet) {
  return StyleSheet.create({
    page: {
      ...fonts.regular,
      paddingTop: PAGE_MARGIN_Y,
      paddingBottom: PAGE_MARGIN_Y + 14,
      paddingHorizontal: PAGE_MARGIN_X,
      fontSize: SHEET_SIZES.body,
      color: SHEET_COLORS.ink,
    },
    bold: fonts.bold,
    italic: fonts.italic,
    boldItalic: fonts.boldItalic,
    mono: fonts.mono,
    muted: {
      color: SHEET_COLORS.muted,
    },
    small: SMALL_TEXT,
    link: {
      color: SHEET_COLORS.ink,
      textDecoration: 'underline',
    },
    title: {
      ...fonts.bold,
      fontSize: SHEET_SIZES.title,
      lineHeight: 1.15,
    },
    titleMeta: {
      ...BODY_TEXT,
      marginTop: 4,
      paddingBottom: 8,
      borderBottomWidth: 1,
      borderBottomColor: SHEET_COLORS.rule,
      color: SHEET_COLORS.muted,
    },
    sectionHeading: {
      ...fonts.bold,
      fontSize: SHEET_SIZES.heading,
      lineHeight: 1.2,
      marginTop: 10,
      marginBottom: 4,
    },
    subHeading: {
      ...BODY_TEXT,
      ...fonts.bold,
      marginTop: 6,
      marginBottom: 2,
    },
    paragraph: {
      ...BODY_TEXT,
      marginBottom: 6,
    },
    blockquote: {
      marginBottom: 6,
      paddingLeft: 8,
      borderLeftWidth: 2,
      borderLeftColor: SHEET_COLORS.rule,
    },
    blockquoteText: {
      ...BODY_TEXT,
      color: SHEET_COLORS.muted,
    },
    strike: {
      textDecoration: 'line-through',
    },
    list: {
      marginBottom: 6,
    },
    listItem: {
      flexDirection: 'row',
      marginBottom: 2,
    },
    listMarker: {
      ...BODY_TEXT,
      width: 14,
    },
    listBody: {
      ...BODY_TEXT,
      flex: 1,
    },
    table: {
      marginTop: 2,
      marginBottom: 8,
    },
    tableHeaderRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: SHEET_COLORS.rule,
      paddingBottom: 2,
    },
    tableHeaderText: {
      ...SMALL_TEXT,
      textTransform: 'uppercase',
      color: SHEET_COLORS.muted,
    },
    tableRow: {
      flexDirection: 'row',
      paddingVertical: 3,
      borderBottomWidth: 0.5,
      borderBottomColor: SHEET_COLORS.rule,
    },
    tableCellText: {
      ...BODY_TEXT,
    },
    missing: {
      ...fonts.bold,
      color: SHEET_COLORS.accent,
    },
    footer: {
      position: 'absolute',
      left: PAGE_MARGIN_X,
      right: PAGE_MARGIN_X,
      bottom: 20,
      flexDirection: 'row',
      justifyContent: 'flex-end',
      ...SMALL_TEXT,
      color: SHEET_COLORS.muted,
    },
  })
}

export type PdfStyles = ReturnType<typeof createPdfStyles>

export type PdfColumnAlign = 'left' | 'right' | 'center'

export function pdfCellStyle({
  align,
  last,
  width,
}: {
  align: PdfColumnAlign
  last: boolean
  width: string
}): Style {
  return {
    width,
    paddingRight: last ? 0 : 6,
    textAlign: align,
  }
}
